import {
  PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON,
  PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ,
  PORTFOLIO_PRICE_DECISION_STATE_LABELS,
  PORTFOLIO_PRICE_DECISION_STATE_OBJ,
  PORTFOLIO_PRICE_DECISION_STATE_VARIANTS,
  PORTFOLIO_VALUATION_REASON,
  PORTFOLIO_VALUATION_REASON_LABELS,
  PORTFOLIO_VALUATION_REASON_OBJ,
  PORTFOLIO_VALUATION_REASON_VARIANTS,
} from "./portfolio.constants";
import type { ChipVariant } from "../types/smart-table";

/** Quality belongs to each input and metric, rather than to the portfolio as a whole. */
export const PORTFOLIO_METRIC_QUALITY = ["confirmed", "estimated", "partial", "unknown"] as const;
export type TPortfolioMetricQuality = (typeof PORTFOLIO_METRIC_QUALITY)[number];
export const PORTFOLIO_METRIC_QUALITY_OBJ = {
  CONFIRMED: "confirmed", ESTIMATED: "estimated", PARTIAL: "partial", UNKNOWN: "unknown",
} as const satisfies Record<string, TPortfolioMetricQuality>;
export const PORTFOLIO_METRIC_QUALITY_LABELS: Record<TPortfolioMetricQuality, Record<string, string>> = {
  confirmed: { "en-US": "Confirmed", "ru-RU": "Подтверждено" },
  estimated: { "en-US": "Estimated", "ru-RU": "Оценочно" },
  partial: { "en-US": "Known part only", "ru-RU": "Известна только часть" },
  unknown: { "en-US": "Unknown", "ru-RU": "Неизвестно" },
};
export const PORTFOLIO_METRIC_QUALITY_VARIANTS: Record<TPortfolioMetricQuality, ChipVariant> = {
  confirmed: "emerald", estimated: "outline", partial: "secondary", unknown: "destructive",
};
export const PORTFOLIO_AVAILABLE_METRIC_QUALITY = [
  PORTFOLIO_METRIC_QUALITY_OBJ.CONFIRMED, PORTFOLIO_METRIC_QUALITY_OBJ.ESTIMATED,
] as const;
export type TPortfolioAvailableMetricQuality = (typeof PORTFOLIO_AVAILABLE_METRIC_QUALITY)[number];
export const PORTFOLIO_UNAVAILABLE_METRIC_QUALITY = [
  PORTFOLIO_METRIC_QUALITY_OBJ.PARTIAL, PORTFOLIO_METRIC_QUALITY_OBJ.UNKNOWN,
] as const;
export type TPortfolioUnavailableMetricQuality = (typeof PORTFOLIO_UNAVAILABLE_METRIC_QUALITY)[number];

/** These inputs are assessed independently, including cash separately from holdings. */
export const PORTFOLIO_METRIC_INPUT = [
  "holdings", "marketPrice", "costBasis", "grossIncome", "netIncome", "tax",
  "cashBalance", "externalFlows", "fxRates", "commissions",
] as const;
export type TPortfolioMetricInput = (typeof PORTFOLIO_METRIC_INPUT)[number];
export const PORTFOLIO_METRIC_INPUT_OBJ = {
  HOLDINGS: "holdings", MARKET_PRICE: "marketPrice", COST_BASIS: "costBasis",
  GROSS_INCOME: "grossIncome", NET_INCOME: "netIncome", TAX: "tax",
  CASH_BALANCE: "cashBalance", EXTERNAL_FLOWS: "externalFlows", FX_RATES: "fxRates", COMMISSIONS: "commissions",
} as const satisfies Record<string, TPortfolioMetricInput>;
export const PORTFOLIO_METRIC_INPUT_LABELS: Record<TPortfolioMetricInput, Record<string, string>> = {
  holdings: { "en-US": "Holdings", "ru-RU": "Количество инструментов" },
  marketPrice: { "en-US": "Market prices", "ru-RU": "Рыночные цены" },
  costBasis: { "en-US": "Acquisition cost", "ru-RU": "Себестоимость" },
  grossIncome: { "en-US": "Gross income", "ru-RU": "Доход до налога" },
  netIncome: { "en-US": "Net income", "ru-RU": "Чистое поступление" },
  tax: { "en-US": "Taxes", "ru-RU": "Налоги" },
  cashBalance: { "en-US": "Cash balances", "ru-RU": "Денежные остатки" },
  externalFlows: { "en-US": "External capital flows", "ru-RU": "Внешние вложения и выводы" },
  fxRates: { "en-US": "Exchange rates", "ru-RU": "Курсы валют" },
  commissions: { "en-US": "Commissions", "ru-RU": "Комиссии" },
};
export const PORTFOLIO_METRIC_INPUT_VARIANTS: Record<TPortfolioMetricInput, ChipVariant> = {
  holdings: "default", marketPrice: "default", costBasis: "secondary",
  grossIncome: "secondary", netIncome: "default", tax: "outline",
  cashBalance: "default", externalFlows: "secondary", fxRates: "outline", commissions: "outline",
};
/** XIRR does not require cost basis, gross income, or tax when these inputs suffice. */
export const PORTFOLIO_XIRR_INPUT = [
  PORTFOLIO_METRIC_INPUT_OBJ.HOLDINGS, PORTFOLIO_METRIC_INPUT_OBJ.MARKET_PRICE,
  PORTFOLIO_METRIC_INPUT_OBJ.CASH_BALANCE, PORTFOLIO_METRIC_INPUT_OBJ.EXTERNAL_FLOWS,
  PORTFOLIO_METRIC_INPUT_OBJ.FX_RATES,
] as const;
export const PORTFOLIO_INCOME_INPUT = [
  PORTFOLIO_METRIC_INPUT_OBJ.GROSS_INCOME, PORTFOLIO_METRIC_INPUT_OBJ.NET_INCOME,
  PORTFOLIO_METRIC_INPUT_OBJ.TAX,
] as const;

export const PORTFOLIO_METRIC_ID = [
  "cash", "marketValue", "costBasis", "invested", "realizedPnL", "unrealizedPnL",
  "grossIncome", "netIncome", "taxes", "totalReturn", "totalReturnNet", "dividendYield", "xirr", "commissions", "deposits", "withdrawals",
] as const;
export type TPortfolioMetricId = (typeof PORTFOLIO_METRIC_ID)[number];
export const PORTFOLIO_METRIC_ID_OBJ = {
  CASH: "cash", MARKET_VALUE: "marketValue", COST_BASIS: "costBasis", INVESTED: "invested",
  REALIZED_PNL: "realizedPnL", UNREALIZED_PNL: "unrealizedPnL", GROSS_INCOME: "grossIncome",
  NET_INCOME: "netIncome", TAXES: "taxes", TOTAL_RETURN: "totalReturn", TOTAL_RETURN_NET: "totalReturnNet",
  DIVIDEND_YIELD: "dividendYield", XIRR: "xirr",
  COMMISSIONS: "commissions", DEPOSITS: "deposits", WITHDRAWALS: "withdrawals",
} as const satisfies Record<string, TPortfolioMetricId>;
export const PORTFOLIO_METRIC_ID_LABELS: Record<TPortfolioMetricId, Record<string, string>> = {
  cash: { "en-US": "Cash", "ru-RU": "Деньги" },
  marketValue: { "en-US": "Market value", "ru-RU": "Рыночная стоимость" },
  costBasis: { "en-US": "Cost basis", "ru-RU": "Себестоимость" },
  invested: { "en-US": "Invested", "ru-RU": "Вложено" },
  realizedPnL: { "en-US": "Realized result", "ru-RU": "Результат закрытых сделок" },
  unrealizedPnL: { "en-US": "Unrealized result", "ru-RU": "Результат открытых позиций" },
  grossIncome: { "en-US": "Gross income", "ru-RU": "Доход до налога" },
  netIncome: { "en-US": "Net income", "ru-RU": "Чистое поступление" },
  taxes: { "en-US": "Taxes", "ru-RU": "Налоги" },
  totalReturn: { "en-US": "Total return before tax", "ru-RU": "Общий результат до налогов" },
  totalReturnNet: { "en-US": "Total return net", "ru-RU": "Общий результат после налогов" },
  dividendYield: { "en-US": "Dividend yield", "ru-RU": "Дивидендная доходность" },
  xirr: { "en-US": "Annualized return", "ru-RU": "Годовая доходность" },
  commissions: { "en-US": "Commissions", "ru-RU": "Комиссии" },
  deposits: { "en-US": "Deposits", "ru-RU": "Пополнения" },
  withdrawals: { "en-US": "Withdrawals", "ru-RU": "Выводы" },
};
export const PORTFOLIO_METRIC_ID_VARIANTS: Record<TPortfolioMetricId, ChipVariant> = {
  cash: "default", marketValue: "default", costBasis: "secondary", invested: "secondary",
  realizedPnL: "default", unrealizedPnL: "default", grossIncome: "secondary", netIncome: "default",
  taxes: "outline", totalReturn: "secondary", totalReturnNet: "default", dividendYield: "outline", xirr: "outline",
  commissions: "outline", deposits: "default", withdrawals: "secondary",
};
export const PORTFOLIO_PRE_TAX_METRIC_ID = [
  PORTFOLIO_METRIC_ID_OBJ.GROSS_INCOME, PORTFOLIO_METRIC_ID_OBJ.TAXES,
  PORTFOLIO_METRIC_ID_OBJ.TOTAL_RETURN, PORTFOLIO_METRIC_ID_OBJ.DIVIDEND_YIELD,
] as const;

/** Existing reason values retain their spelling and labels across the extended contract. */
export const PORTFOLIO_METRIC_REASON = [
  ...PORTFOLIO_VALUATION_REASON,
  ...PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON,
  PORTFOLIO_PRICE_DECISION_STATE_OBJ.STALE,
  PORTFOLIO_PRICE_DECISION_STATE_OBJ.MANUAL,
  PORTFOLIO_PRICE_DECISION_STATE_OBJ.IDENTITY_CONFLICT,
  PORTFOLIO_PRICE_DECISION_STATE_OBJ.EVENT_PENDING,
  "missing_cost_basis", "missing_gross_income", "missing_net_income", "missing_tax",
  "missing_cash_balance", "missing_external_flows", "missing_holdings", "missing_commissions",
  "incompatible_units", "unconfirmed_option_event", "unsupported_short", "future_price",
] as const;
export type TPortfolioMetricReason = (typeof PORTFOLIO_METRIC_REASON)[number];
export const PORTFOLIO_METRIC_REASON_OBJ = {
  ...PORTFOLIO_VALUATION_REASON_OBJ,
  ...PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ,
  STALE_PRICE: PORTFOLIO_PRICE_DECISION_STATE_OBJ.STALE,
  MANUAL_PRICE: PORTFOLIO_PRICE_DECISION_STATE_OBJ.MANUAL,
  IDENTITY_CONFLICT: PORTFOLIO_PRICE_DECISION_STATE_OBJ.IDENTITY_CONFLICT,
  EVENT_PENDING: PORTFOLIO_PRICE_DECISION_STATE_OBJ.EVENT_PENDING,
  MISSING_COST_BASIS: "missing_cost_basis", MISSING_GROSS_INCOME: "missing_gross_income",
  MISSING_NET_INCOME: "missing_net_income", MISSING_TAX: "missing_tax",
  MISSING_CASH_BALANCE: "missing_cash_balance", MISSING_EXTERNAL_FLOWS: "missing_external_flows",
  MISSING_COMMISSIONS: "missing_commissions",
  MISSING_HOLDINGS: "missing_holdings", INCOMPATIBLE_UNITS: "incompatible_units",
  UNCONFIRMED_OPTION_EVENT: "unconfirmed_option_event", UNSUPPORTED_SHORT: "unsupported_short",
  FUTURE_PRICE: "future_price",
} as const satisfies Record<string, TPortfolioMetricReason>;
export const PORTFOLIO_METRIC_REASON_LABELS: Record<TPortfolioMetricReason, Record<string, string>> = {
  ...PORTFOLIO_VALUATION_REASON_LABELS,
  value_unavailable: { "en-US": "Value unavailable", "ru-RU": "Значение недоступно" },
  coverage_unknown: { "en-US": "Coverage unknown", "ru-RU": "Полнота данных неизвестна" },
  coverage_changed: { "en-US": "Coverage changed", "ru-RU": "Состав данных изменился" },
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.STALE]: PORTFOLIO_PRICE_DECISION_STATE_LABELS.stale,
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.MANUAL]: PORTFOLIO_PRICE_DECISION_STATE_LABELS.manual,
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.IDENTITY_CONFLICT]: PORTFOLIO_PRICE_DECISION_STATE_LABELS["identity-conflict"],
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.EVENT_PENDING]: PORTFOLIO_PRICE_DECISION_STATE_LABELS["event-pending"],
  missing_cost_basis: { "en-US": "Acquisition cost unknown", "ru-RU": "Себестоимость неизвестна" },
  missing_gross_income: { "en-US": "Gross income unknown", "ru-RU": "Доход до налога неизвестен" },
  missing_net_income: { "en-US": "Net income unknown", "ru-RU": "Чистое поступление неизвестно" },
  missing_tax: { "en-US": "Tax unknown", "ru-RU": "Налог неизвестен" },
  missing_cash_balance: { "en-US": "Cash balance unconfirmed", "ru-RU": "Денежный остаток не подтверждён" },
  missing_commissions: { "en-US": "Broker expenses unconfirmed", "ru-RU": "Расходы брокера не подтверждены" },
  missing_external_flows: { "en-US": "External capital history incomplete", "ru-RU": "История вложений и выводов неполна" },
  missing_holdings: { "en-US": "Holdings unconfirmed", "ru-RU": "Количество инструментов не подтверждено" },
  incompatible_units: { "en-US": "Instrument units conflict", "ru-RU": "Единицы инструмента не согласованы" },
  unconfirmed_option_event: { "en-US": "Option event unconfirmed", "ru-RU": "Событие опциона не подтверждено" },
  unsupported_short: { "en-US": "Short position requires review", "ru-RU": "Короткая позиция требует разбора" },
  future_price: { "en-US": "Price is later than the valuation date", "ru-RU": "Цена позднее даты оценки" },
};
export const PORTFOLIO_METRIC_REASON_VARIANTS: Record<TPortfolioMetricReason, ChipVariant> = {
  ...PORTFOLIO_VALUATION_REASON_VARIANTS,
  value_unavailable: "destructive", coverage_unknown: "outline", coverage_changed: "secondary",
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.STALE]: PORTFOLIO_PRICE_DECISION_STATE_VARIANTS.stale,
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.MANUAL]: PORTFOLIO_PRICE_DECISION_STATE_VARIANTS.manual,
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.IDENTITY_CONFLICT]: PORTFOLIO_PRICE_DECISION_STATE_VARIANTS["identity-conflict"],
  [PORTFOLIO_PRICE_DECISION_STATE_OBJ.EVENT_PENDING]: PORTFOLIO_PRICE_DECISION_STATE_VARIANTS["event-pending"],
  missing_cost_basis: "destructive", missing_gross_income: "outline", missing_net_income: "destructive",
  missing_tax: "outline", missing_cash_balance: "destructive", missing_external_flows: "destructive", missing_commissions: "destructive",
  missing_holdings: "destructive", incompatible_units: "destructive", unconfirmed_option_event: "destructive",
  unsupported_short: "destructive", future_price: "destructive",
};
export const PORTFOLIO_ESTIMATE_METRIC_REASON = [
  PORTFOLIO_METRIC_REASON_OBJ.STALE_PRICE, PORTFOLIO_METRIC_REASON_OBJ.MANUAL_PRICE,
] as const;
